// Kleine bouwstenen: Icoon, Knop, Segment, Chip, Pil, Vlak, Strook, LegeStaat,
// Pagineer, Veld. Alleen klassen uit bouwstenen.css; geen kleur of maat in
// style={{…}} (DESIGN.md regel 1).

import type { LucideIcon } from "lucide-react";
import { ChevronLeft, ChevronRight, Sparkles, X } from "lucide-react";
import type { ButtonHTMLAttributes, HTMLAttributes, InputHTMLAttributes, ReactNode } from "react";

/** Het icoon van de AI-agent ("Vraag de bronnen"), overal hetzelfde (ronde 5,
 *  Clipy hooc06uw015x 0:11–0:22). Gebruik het nergens anders voor. */
export const AiIcoon: LucideIcon = Sparkles;

/** Eén iconenfamilie (patroon 6): Lucide, lijn 1,7. De maat volgt --icoon. */
export function Icoon({ als: Als, klein = false, label }: { als: LucideIcon; klein?: boolean; label?: string }) {
  return (
    <Als
      className="s-icoon"
      size={klein ? 16 : 20}
      strokeWidth={1.7}
      aria-hidden={label ? undefined : true}
      aria-label={label}
    />
  );
}

type KnopVariant = "stil" | "primair" | "zacht" | "icoon" | "link";

export function Knop({
  variant = "stil",
  actief = false,
  icoon,
  children,
  className,
  ...rest
}: {
  variant?: KnopVariant;
  actief?: boolean;
  icoon?: LucideIcon;
  children?: ReactNode;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  const klassen = ["s-knop", variant !== "stil" ? `s-knop--${variant}` : "", actief ? "s-knop--actief" : "", className ?? ""]
    .filter(Boolean)
    .join(" ");
  return (
    <button type="button" className={klassen} {...rest}>
      {icoon && <Icoon als={icoon} />}
      {children}
    </button>
  );
}

/** Een handvol keuzeknoppen op één toon (weergave, sorteren). */
export function Segment<T extends string>({
  opties,
  waarde,
  onKies,
  label,
}: {
  opties: { waarde: T; label: ReactNode; uitleg?: string; icoon?: LucideIcon }[];
  waarde: T;
  onKies: (w: T) => void;
  label: string;
}) {
  return (
    <div className="s-segment" role="group" aria-label={label}>
      {opties.map((o) => (
        <button
          key={o.waarde}
          type="button"
          className="s-knop"
          aria-pressed={o.waarde === waarde}
          title={o.uitleg}
          onClick={() => onKies(o.waarde)}
        >
          {o.icoon && <Icoon als={o.icoon} klein />}
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Filterchip (patroon 8): rond, zonder rand, gekozen = indigo. */
export function Chip({
  aan,
  onClick,
  children,
  tel,
  onWeg,
  uitleg,
  lat = true,
}: {
  aan?: boolean;
  onClick?: () => void;
  children: ReactNode;
  tel?: number;
  /** Met kruisje: de chip haalt een gekozen filter weg. */
  onWeg?: () => void;
  uitleg?: string;
  lat?: boolean;
}) {
  return (
    <button
      type="button"
      className="s-chip"
      aria-pressed={onWeg ? undefined : !!aan}
      data-lat={lat ? "chip" : undefined}
      title={uitleg}
      onClick={onWeg ?? onClick}
    >
      {children}
      {tel !== undefined && <span className="s-chip__tel">{tel.toLocaleString("nl-NL")}</span>}
      {onWeg && (
        <span className="s-chip__weg" aria-label="weghalen">
          <Icoon als={X} klein />
        </span>
      )}
    </button>
  );
}

export type Toon = "neutraal" | "accent" | "groen" | "oranje" | "rood" | "blauw";

/** Label op toon; betekeniskleur alleen hier (en in cijfers en grafieken). */
export function Pil({ toon = "neutraal", children, uitleg }: { toon?: Toon; children: ReactNode; uitleg?: string }) {
  return (
    <span className={`s-pil${toon !== "neutraal" ? ` s-pil--${toon}` : ""}`} title={uitleg}>
      {children}
    </span>
  );
}

/** Een vlak op toon: wat vroeger een kaart met rand was. */
export function Vlak({ children, className, ...rest }: { children: ReactNode; className?: string } & HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={`s-vlak${className ? ` ${className}` : ""}`} {...rest}>
      {children}
    </div>
  );
}

/** Een brede band op toon (pulsbalk, overzichtsstrook). */
export function Strook({ children, rechts, className }: { children: ReactNode; rechts?: ReactNode; className?: string }) {
  return (
    <div className={`s-strook${className ? ` ${className}` : ""}`}>
      <div className="s-strook__inhoud">{children}</div>
      {rechts}
    </div>
  );
}

export function LegeStaat({ zin, actie, onActie }: { zin: string; actie?: string; onActie?: () => void }) {
  return (
    <div className="s-leeg">
      <p>{zin}</p>
      {actie && onActie && (
        <Knop variant="zacht" onClick={onActie}>
          {actie}
        </Knop>
      )}
    </div>
  );
}

export function Pagineer({
  nr,
  van,
  onNr,
  tekst,
}: {
  nr: number;
  van: number;
  onNr: (n: number) => void;
  /** Wat er tussen de knoppen staat, bv. "1–15 van 40". */
  tekst: string;
}) {
  if (van <= 1) return null;
  return (
    <nav className="s-pagineer" aria-label="Pagina's">
      <Knop variant="icoon" aria-label="Vorige pagina" disabled={nr <= 1} onClick={() => onNr(nr - 1)} icoon={ChevronLeft} />
      <span>{tekst}</span>
      <Knop variant="icoon" aria-label="Volgende pagina" disabled={nr >= van} onClick={() => onNr(nr + 1)} icoon={ChevronRight} />
    </nav>
  );
}

/** Klein invoerveld, alleen binnen een menu (het ene grote veld is Zoekveld). */
export function Veld(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className="s-veld" {...props} />;
}
